import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { laboratoryService } from "../../services/laboratory.services";

// Helper funkcia na získanie limitov podľa pohlavia
const getGenderLimits = (parameterDef, patientGender) => {
  if (parameterDef?.gender_limits?.length > 0 && patientGender) {
    const genderLimit = parameterDef.gender_limits.find(
      (gl) => gl.gender === patientGender,
    );
    if (genderLimit) {
      return {
        lower: parseFloat(genderLimit.lower_limit),
        upper: parseFloat(genderLimit.upper_limit),
      };
    }
  }
  return {
    lower: parameterDef?.lower_limit
      ? parseFloat(parameterDef.lower_limit)
      : null,
    upper: parameterDef?.upper_limit
      ? parseFloat(parameterDef.upper_limit)
      : null,
  };
};

// Helper funkcia na kontrolu či je hodnota mimo rozsah
const isValueOutOfRange = (value, limits) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue) || (limits.lower === null && limits.upper === null)) {
    return false;
  }
  if (limits.lower !== null && numValue < limits.lower) {
    return true;
  }
  if (limits.upper !== null && numValue > limits.upper) {
    return true;
  }
  return false;
};

export const ComplementTrends = ({
  samplings,
  onCopyToClipboard,
  patientGender,
}) => {
  const [expandedAreas, setExpandedAreas] = useState({});
  const [selectedParameters, setSelectedParameters] = useState({});
  const [parameterDefinitions, setParameterDefinitions] = useState([]);
  const [laboratoryAreas, setLaboratoryAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Načítanie dát z API
  useEffect(() => {
    const loadApiData = async () => {
      try {
        setIsLoading(true);
        const [paramDefsResponse, labAreasResponse] = await Promise.all([
          laboratoryService.getParameterDefinitions(),
          laboratoryService.getLaboratoryAreas(),
        ]);

        setParameterDefinitions(paramDefsResponse.results || []);
        setLaboratoryAreas(labAreasResponse.results || []);
      } catch (error) {
        console.error("Chyba pri načítaní dát z API:", error);
        // Fallback na prázdne pole, použije sa pôvodná logika
        setParameterDefinitions([]);
        setLaboratoryAreas([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadApiData();
  }, []);

  // Vytvorenie lookup mapy pre parameter definitions
  const parameterDefinitionsMap = React.useMemo(() => {
    const map = {};
    parameterDefinitions.forEach((paramDef) => {
      map[paramDef.id] = paramDef;
    });
    return map;
  }, [parameterDefinitions]);

  // Zoskupenie výsledkov podľa laboratórnych oblastí
  const groupByLaboratoryArea = () => {
    const areas = {};

    samplings.forEach((sampling) => {
      sampling.parameter_results?.forEach((result) => {
        let areaName = "Neznámá oblast";

        // Pokúsime sa namapovať pomocí API dát
        if (
          result.parameter_definition &&
          parameterDefinitionsMap[result.parameter_definition]
        ) {
          const paramDef = parameterDefinitionsMap[result.parameter_definition];
          areaName = paramDef.area_name || "Neznámá oblast";
        } else {
          // Fallback na pôvodnú simulačnú logiku ak nie sú dostupné API dáta
          areaName = "Biochemie";
          if (
            result.parameter_name.includes("Hemoglobin") ||
            result.parameter_name.includes("Leukocyty") ||
            result.parameter_name.includes("Erytrocyty")
          ) {
            areaName = "Hematologie";
          } else if (result.parameter_name.includes("Toxikologie")) {
            areaName = "Toxikologie";
          }
        }

        if (!areas[areaName]) {
          areas[areaName] = {};
        }

        const paramKey = `${result.parameter_name}_${result.parameter_abbreviation}`;
        if (!areas[areaName][paramKey]) {
          areas[areaName][paramKey] = {
            name: result.parameter_name,
            abbreviation: result.parameter_abbreviation,
            units: result.parameter_units,
            results: [],
          };
        }

        areas[areaName][paramKey].results.push({
          ...result,
          made_at: sampling.made_at,
          sampling_id: sampling.id,
        });
      });
    });

    // Zoradenie výsledkov podľa dátumu
    Object.keys(areas).forEach((areaName) => {
      Object.keys(areas[areaName]).forEach((paramKey) => {
        areas[areaName][paramKey].results.sort(
          (a, b) => new Date(b.made_at) - new Date(a.made_at),
        );

        // Prepočítanie is_out_of_range na základe gender_limits
        const paramDef =
          parameterDefinitionsMap[
            areas[areaName][paramKey].results[0]?.parameter_definition
          ];
        if (paramDef && patientGender) {
          const limits = getGenderLimits(paramDef, patientGender);
          areas[areaName][paramKey].results = areas[areaName][
            paramKey
          ].results.map((result) => ({
            ...result,
            is_out_of_range: isValueOutOfRange(result.value, limits),
            gender_limits: limits,
          }));
          areas[areaName][paramKey].limits = limits;
        }
      });
    });

    return areas;
  };

  const toggleArea = (areaName) => {
    setExpandedAreas((prev) => ({
      ...prev,
      [areaName]: !prev[areaName],
    }));
  };

  const toggleParameterSelection = (areaName, paramKey) => {
    const key = `${areaName}_${paramKey}`;
    setSelectedParameters((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const copySelectedParameters = () => {
    const areas = groupByLaboratoryArea();
    const selectedResults = [];

    Object.keys(selectedParameters).forEach((key) => {
      if (selectedParameters[key]) {
        // Rozdelíme kľúč správne - prvá časť je areaName, zvyšok je paramKey
        const keyParts = key.split("_");
        const areaName = keyParts[0];
        const paramKey = keyParts.slice(1).join("_");

        const parameter = areas[areaName]?.[paramKey];
        if (parameter) {
          parameter.results.forEach((result) => {
            selectedResults.push({
              parameter_name: parameter.name,
              value: result.value,
              parameter_units: parameter.units,
              made_at: result.made_at,
            });
          });
        }
      }
    });

    if (selectedResults.length > 0) {
      onCopyToClipboard(selectedResults);
    }
  };

  const clearAllSelectedParameters = () => {
    setSelectedParameters({});
  };

  const areas = groupByLaboratoryArea();

  // Funkcia na získanie grafov pre zaškrtnuté parametre
  const getSelectedParametersCharts = () => {
    const selectedCharts = [];

    Object.keys(selectedParameters).forEach((key) => {
      if (selectedParameters[key]) {
        // Rozdelíme kľúč správne - prvá časť je areaName, zvyšok je paramKey
        const keyParts = key.split("_");
        const areaName = keyParts[0];
        const paramKey = keyParts.slice(1).join("_");

        const parameter = areas[areaName]?.[paramKey];
        if (parameter) {
          selectedCharts.push({
            areaName,
            paramKey,
            parameter,
            key: key,
          });
        }
      }
    });

    return selectedCharts;
  };

  const selectedCharts = getSelectedParametersCharts();

  // Zobrazenie loading stavu
  if (isLoading) {
    return (
      <div className="complement-trends">
        <div
          className="loading-container"
          style={{ padding: "20px", textAlign: "center" }}
        >
          <p>Načítám data laboratorních oblastí...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="complement-trends">
      <div className="trends-header">
        <div className="selected-actions">
          {Object.values(selectedParameters).some((selected) => selected) && (
            <>
              <button
                className="button btn-primary"
                onClick={copySelectedParameters}
              >
                Kopírovat vybrané parametry
              </button>
              <button
                className="button btn-light"
                onClick={clearAllSelectedParameters}
              >
                Zrušit výběr všech
              </button>
            </>
          )}
        </div>
      </div>

      <div className="trends-layout">
        {/* Ľavá strana - Tabulky */}
        <div className="trends-left">
          <div className="laboratory-areas">
            {Object.entries(areas).map(([areaName, parameters]) => (
              <div key={areaName} className="laboratory-area">
                <div
                  className="area-header"
                  onClick={() => toggleArea(areaName)}
                >
                  <h3>{areaName}</h3>
                  <span
                    className={`expand-icon ${
                      expandedAreas[areaName] ? "expanded" : ""
                    }`}
                  >
                    ▼
                  </span>
                </div>

                {expandedAreas[areaName] && (
                  <div className="area-content">
                    <div className="parameters-grid">
                      {Object.entries(parameters).map(
                        ([paramKey, parameter]) => (
                          <div key={paramKey} className="parameter-section">
                            <div className="parameter-header">
                              <div className="parameter-info">
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedParameters[
                                      `${areaName}_${paramKey}`
                                    ] || false
                                  }
                                  onChange={() =>
                                    toggleParameterSelection(areaName, paramKey)
                                  }
                                />
                                <span className="parameter-name">
                                  {parameter.name} ({parameter.abbreviation})
                                </span>
                              </div>
                              <button
                                className="button btn-sm"
                                onClick={() => {
                                  const formattedResults =
                                    parameter.results.map((result) => ({
                                      parameter_name: parameter.name,
                                      value: result.value,
                                      parameter_units: parameter.units,
                                      made_at: result.made_at,
                                    }));
                                  onCopyToClipboard(formattedResults);
                                }}
                              >
                                Kopírovat
                              </button>
                            </div>

                            <div className="parameter-results-table">
                              <div className="table-header">
                                <div className="header-cell">Datum</div>
                                <div className="header-cell">Hodnota</div>
                                <div className="header-cell">Jednotka</div>
                              </div>

                              <div className="table-body">
                                {parameter.results
                                  .slice(0, 10)
                                  .map((result, index) => (
                                    <div
                                      key={`${result.sampling_id}_${result.id}_${index}`}
                                      className={`table-row ${
                                        result.is_out_of_range
                                          ? "out-of-range"
                                          : ""
                                      }`}
                                    >
                                      <div className="cell">
                                        {new Date(
                                          result.made_at,
                                        ).toLocaleDateString("cs-CZ")}
                                      </div>
                                      <div className="cell">{result.value}</div>
                                      <div className="cell">
                                        {parameter.units}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Pravá strana - Grafy zaškrtnutých parametrov */}
        <div className="trends-right">
          <div className="charts-container">
            {selectedCharts.length > 0 ? (
              <>
                <h3 className="charts-title">Grafy vybraných parametrů</h3>
                {selectedCharts.map(
                  ({ areaName, paramKey, parameter, key }) => (
                    <div key={key} className="parameter-chart">
                      <h4>
                        {parameter.name} ({parameter.abbreviation}) - {areaName}
                      </h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart
                          data={parameter.results
                            .slice()
                            .reverse()
                            .map((result) => ({
                              date: new Date(result.made_at).toLocaleDateString(
                                "cs-CZ",
                              ),
                              value: parseFloat(result.value) || 0,
                              isOutOfRange: result.is_out_of_range,
                            }))}
                          margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 20,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            label={{
                              value: parameter.units,
                              angle: -90,
                              position: "insideLeft",
                            }}
                          />
                          <Tooltip
                            formatter={(value, name) => [
                              `${value} ${parameter.units}`,
                              parameter.name,
                            ]}
                            labelFormatter={(label) => `Datum: ${label}`}
                          />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="#2563eb"
                            strokeWidth={2}
                            dot={(props) => {
                              const { cx, cy, payload } = props;
                              return (
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={4}
                                  fill={
                                    payload.isOutOfRange ? "#dc2626" : "#2563eb"
                                  }
                                  stroke={
                                    payload.isOutOfRange ? "#dc2626" : "#2563eb"
                                  }
                                  strokeWidth={2}
                                />
                              );
                            }}
                            activeDot={{ r: 6, fill: "#1d4ed8" }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ),
                )}
              </>
            ) : (
              <div className="no-charts-selected">
                <p>Vyberte parametry v levé části pro zobrazení grafů</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
